/*
 * CipaSignatureBlocker — quando o funcionário tem pendência de assinatura
 * digital CIPA (como Designado ou Supervisor do Contrato), bloqueia o app
 * e permite assinar ali mesmo via OTP (SMS).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { FaceSignCapture } from "./FaceSignCapture";
import {
  ShieldCheck, Loader2, Smartphone, FileSignature, ScanFace,
} from "lucide-react";

export default function CipaSignatureBlocker() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("start");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/employee/cipa-pending-signatures`, {
        params: { token }, timeout: 15000,
      });
      setItems(r.data?.items || []);
      setStep("start");
      setCode("");
    } catch {
      setItems([]); // fail open
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading || items.length === 0) return null;

  const current = items[0];
  const isSup = current.kind === "supervisor";

  const sendOtp = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/public/${current.kind}-signature/${current.token}/send-otp`,
      );
      setStep("code");
      toast.success(`Código enviado por SMS para ${r.data.telefone_masked}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar o código");
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/public/${current.kind}-signature/${current.token}/confirm`,
        { code: code.trim() },
      );
      toast.success("Assinatura digital registrada!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido ou expirado");
    }
    setBusy(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[27] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="cipa-signature-blocker">
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <FileSignature className="w-6 h-6 text-amber-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-slate-900 text-lg">
              {isSup
                ? "Assinatura: Supervisor do Contrato"
                : "Assinatura: Designado da CIPA"}
            </h2>
            <p className="text-xs text-slate-500">
              {isSup
                ? "Você foi indicado Supervisor do Contrato nas cartas de Designado CIPA. Assine digitalmente para liberar a emissão."
                : "Assine digitalmente a sua carta de Designado da CIPA (NR-05)."}
            </p>
          </div>
        </div>

        <div className="rounded-md bg-amber-50 border border-amber-100 p-3 text-xs text-amber-900 space-y-1">
          <p>🔐 Assine com o seu rosto (selfie) ou com código por SMS</p>
          <p>📄 O carimbo digital (data, IP e dispositivo) entra no documento</p>
          <p>🔓 O acesso ao app será liberado após assinar</p>
        </div>

        {step === "start" ? (
          <div className="space-y-2">
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12"
              onClick={() => setStep("face")}
              disabled={busy}
              data-testid="cipa-blocker-face-btn"
            >
              <ScanFace className="w-4 h-4 mr-2" />
              Assinar com meu rosto (mais fácil)
            </Button>
            <Button
              variant="outline"
              className="w-full h-11 text-amber-700 border-amber-300 hover:bg-amber-50"
              onClick={sendOtp}
              disabled={busy}
              data-testid="cipa-blocker-send-otp-btn"
            >
              {busy
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Smartphone className="w-4 h-4 mr-2" />}
              Receber código por SMS
            </Button>
          </div>
        ) : step === "face" ? (
          <FaceSignCapture
            onCancel={() => setStep("start")}
            onSubmit={async (selfie) => {
              await axios.post(
                `${API}/public/${current.kind}-signature/${current.token}/sign-face`,
                { selfie_base64: selfie, consent: true },
              );
              toast.success("Rosto verificado — assinatura registrada!");
              load();
            }}
          />
        ) : (
          <div className="space-y-3">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="Código de 6 dígitos"
              inputMode="numeric"
              maxLength={8}
              className="text-center text-lg tracking-[0.4em] h-12"
              data-testid="cipa-blocker-code-input"
            />
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12"
              onClick={confirm}
              disabled={busy || code.trim().length < 4}
              data-testid="cipa-blocker-confirm-btn"
            >
              {busy
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <ShieldCheck className="w-4 h-4 mr-2" />}
              Assinar digitalmente
            </Button>
            <button
              className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
              onClick={sendOtp}
              disabled={busy}
              data-testid="cipa-blocker-resend-btn"
            >
              Reenviar código
            </button>
          </div>
        )}

        {items.length > 1 && (
          <p className="text-[11px] text-slate-400 text-center">
            Você tem {items.length} assinaturas pendentes — uma por vez.
          </p>
        )}
      </motion.div>
    </motion.div>
  );
}
